/**
 * 主页面板（主菜单 · 纸白版，2026-10-08 重做）
 * 背景：场景图门面版（bg-home/award/theater 三轮实拍）当日被茄子判「背景不太行」撤图换白底，
 * 回 .panel-root 原生纸面；scene 层从 DOM 撤下但 CSS（.menu-scene 双纱/.menu-hero 柔光雾/
 * .menu-card 纸卡）全部保留待用——日后挂回=恢复 scene 层+img+menuIntro 的 scene 段。
 * 菜单与版本号坐底部纸卡。入场编排 menuIntro（anime.js 门面首个使用点，只播一次）。
 * 无游戏状态依赖，refresh() 时检查存档以启用/禁用「继续游戏」并刷新存档摘要行。
 */
import type { GameApp } from '../../app';
import { saveSystem } from '../../systems/SaveSystem';
import { createDiv, createSpan, createButton, setLabel } from '../ui';
import { menuIntro } from '../anim';

export class MainMenuPanel {
    readonly element: HTMLElement;
    private hero: HTMLElement;
    private card: HTMLElement;
    private continueBtn: HTMLButtonElement;
    private shopBtn: HTMLButtonElement;
    private saveInfo: HTMLSpanElement;
    private introDone = false;

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root main-menu-root');

        const inner = createDiv('menu-inner');

        // 中部：大标题（柔光雾垫字）
        this.hero = createDiv('menu-hero');
        this.hero.appendChild(createDiv('logo-star', ['✦ ✦ ✦']));
        this.hero.appendChild(createDiv('menu-logo', ['影后修炼手札']));
        this.hero.appendChild(createDiv('menu-sub', ['从默默无闻 · 到万众瞩目的星光之旅']));
        inner.appendChild(this.hero);

        // 底部纸卡：菜单 + 版本号
        this.card = createDiv('menu-card');
        const stack = createDiv('menu-stack');
        const newGameBtn = createButton('menu-btn primary', '新游戏', undefined, 'star');
        // 继续按钮＝主行 + 存档摘要两行（摘要 .menu-save-info flex-basis:100% 换行居中；
        // ⛔ 对本钮用 setLabel 重设文案——它会 clearChildren 抹掉摘要行）
        this.continueBtn = createButton('menu-btn ghost menu-btn-continue', '继续游戏', undefined, 'folder');
        this.saveInfo = createSpan('menu-save-info');
        this.continueBtn.appendChild(this.saveInfo);
        this.shopBtn = createButton('menu-btn ghost', '成就商店', undefined, 'trophy');
        stack.appendChild(newGameBtn);
        stack.appendChild(this.continueBtn);
        stack.appendChild(this.shopBtn);
        this.card.appendChild(stack);
        // 存档时机告知（2026-10-28 茄子令「告诉玩家存档时机在哪里」；2026-11-03 同日改口径＝一个月只存一次）：
        // 写盘只落在每月月初的决策日那一格，这里是玩家唯一需要知道这件事的地方——一句话说明，不许执行期再叠常驻提示
        this.card.appendChild(createDiv('menu-savenote', ['自动存档 · 每月初']));
        this.card.appendChild(createDiv('menu-version', [`${__APP_VERSION__} · ${__BUILD__}`]));
        inner.appendChild(this.card);

        panel.appendChild(inner);
        this.element = panel;

        newGameBtn.addEventListener('click', () => this.app.onNewGame());
        this.continueBtn.addEventListener('click', () => this.app.onContinue());
        this.shopBtn.addEventListener('click', () => this.app.show('achievementShop'));
    }

    /** 进入主页：首次播入场编排；每次刷新存档态、摘要行与成就商店解锁态 */
    refresh(): void {
        if (!this.introDone) {
            this.introDone = true;
            menuIntro(this.hero, this.card);
        }
        // load() 内建版本兼容检查：旧版本存档返回 null，视同无档
        const data = saveSystem.load();
        this.continueBtn.disabled = !data;
        this.saveInfo.textContent = data
            // 月存口径（2026-11-03）：档只有月初那一格，摘要不再报周次（报了永远是「第1周初」＝误导）
            ? `${data.player.name} · 第${data.player.year + 1}年${data.player.month}月`
            : '';
        this.saveInfo.style.display = data ? '' : 'none';
        // 一周目完成（达成任意结局）后解锁；否则置灰不可进入
        const meta = saveSystem.loadMeta();
        this.shopBtn.disabled = !meta.shopUnlocked;
        setLabel(this.shopBtn, meta.shopUnlocked
            ? '成就商店'
            : '成就商店 · 完成一周目后开启', 'trophy');
    }

    destroy(): void {
        // 无外部事件监听，无需清理
    }
}
